import { View, TouchableOpacity, StyleSheet, ViewStyle } from 'react-native';
import TextLato from '../text/text-lato/TextLato';
import { useStore } from 'src/contexts/StoreContext';
import { LookupIcon } from 'src/images/LookupIcon';
import { getSelectedLookupTitle } from 'src/screen/picture_view/utils/getSelectedLookupTitle';
import { ILookupModel } from 'src/store/_models/lookup';
import amplitude from 'src/_shared/amplitude';
import { AnalyticsEvents } from 'src/_shared/analyticsEvents';
import { APP_VERSION } from 'src/app/settings';
import { alert, brand, neutrals } from 'src/theme/colors';
import { DYNAMIC_MODAL_VIEWS } from 'src/store/_models/dynamic-modal-layer/dynamicModalLayer.constants';
import { BOTTOM_SHEET_VIEWS } from 'src/store/bottom-sheet/bottomSheet.constants';
import { Icon } from '../icon/Icon';
import { ICON_NAMES } from '../icon/icon.helper';
import { LookupCodes } from 'src/constants/lookup.constants';
import { CircularButton } from '../buttons/circular-button/CircularButton';
import { CIRCULAR_BUTTON_SIZES, CIRCULAR_BUTTON_TYPES } from '../buttons/circular-button/CircularButton.constants';
import { FocusGroups } from '../focus-provider/FocusProvider.constants';
import { TestIDs } from 'src/constants/testIDs.constants';
import { configurationTestID } from 'src/helpers/testIDs.helper';
import { parseAnalyticsParamsByProduct } from 'src/_shared/analytics.utils';
import { BottomSheetScrollView } from '@gorhom/bottom-sheet';

export const BottomSheetLookupsList = ({ bsStyle }: { bsStyle: ViewStyle }): JSX.Element => {
  const {
    project,
    i18n,
    bottomSheet: { openBottomSheet, closeBottomSheet, bottomSheetLookupsSecondLevel },
    dynamicModal: { openModal },
  } = useStore();

  const handleDeleteProduct = () => {
    openModal({
      dynamicModalView: DYNAMIC_MODAL_VIEWS.DeleteProduct,
      onPressDeleteProduct: closeBottomSheet,
    });
    amplitude.trackEvent(AnalyticsEvents.personalize.deleteProductClick, {
      ...parseAnalyticsParamsByProduct(project.activeProduct),
      n_products: project.totalNumberOfProducts,
    });
  };

  const onPressSecondLevelLookup = (lookup: ILookupModel) => {
    openBottomSheet({
      bottomSheetView: BOTTOM_SHEET_VIEWS.LookupOptions,
      bottomSheetTitle: lookup.code,
      bottomSheetLookup: lookup,
      isSecondLevelLookup: true,
    });
  };

  return (
    <BottomSheetScrollView contentContainerStyle={bsStyle}>
      <View style={styles_lookup.scrollContainer}>
        <View style={styles_lookup.titleContainer}>
          <TextLato style={styles_lookup.title}>{i18n.t('customize:other-options')}</TextLato>
          <CircularButton
            onPress={async () => closeBottomSheet()}
            accessible={true}
            accessibilityLabel={i18n.t('actions:close_x', { x: i18n.t('customize:other-options') })}
            accessibilityRole="button"
            iconName={ICON_NAMES.Close}
            iconStyle={[styles_lookup.icon, styles_lookup.closeIcon]}
            size={CIRCULAR_BUTTON_SIZES.SMALL}
            type={CIRCULAR_BUTTON_TYPES.ONLY_ICON}
            testID={configurationTestID(TestIDs.OTHER_OPTIONS, TestIDs.CLOSE)}
            focusId={configurationTestID(TestIDs.OTHER_OPTIONS, TestIDs.CLOSE)}
            focusOrder={0}
            focusGroup={FocusGroups.BOTTOM_SHEET}
          />
        </View>

        <View style={styles_lookup.containerChoices}>
          {bottomSheetLookupsSecondLevel?.map((lookup, index) => (
            <TouchableOpacity
              style={styles_lookup.control}
              onPress={() => onPressSecondLevelLookup(lookup)}
              key={lookup.code}
              accessible={true}
              accessibilityLabel={i18n.t(`actions:open_x`, { x: i18n.t(`lookups:${lookup.code}`) })}
              accessibilityRole="button"
            >
              <View style={styles_lookup.containerChoice} key={lookup.code}>
                <View style={styles_lookup.choiceDetails}>
                  <View style={styles_lookup.icon}>
                    <LookupIcon lookup_code={lookup.code as LookupCodes} />
                  </View>
                  <View style={{ flexDirection: 'row', gap: 8 }}>
                    <TextLato style={styles_lookup.choiceText}>{i18n.t(`lookups:${lookup.code}`)}</TextLato>
                    <View style={styles_lookup.pill}>
                      <TextLato style={styles_lookup.pillTextxt}>
                        {getSelectedLookupTitle(project.activeProduct, lookup.code, { i18n })}
                      </TextLato>
                    </View>
                  </View>
                </View>
                <View style={styles_lookup.arrowIcon}>
                  <Icon name={ICON_NAMES.ArrowRight1} color={brand.main.water} />
                </View>
              </View>
            </TouchableOpacity>
          ))}
        </View>
        <View
          style={{
            paddingBottom: 24,
            borderTopWidth: 1,
            borderColor: neutrals.neutralGrays.dust,
          }}
        >
          {!project.isProjectAddedToCart && (
            <TouchableOpacity
              style={{ marginTop: 18 }}
              onPress={handleDeleteProduct}
              accessible={true}
              accessibilityLabel={i18n.t('actions:remove_x', { x: i18n.t('general:frame') })}
              accessibilityRole="button"
            >
              <View style={[styles_lookup.containerChoice]}>
                <View style={[styles_lookup.choiceDetails]}>
                  <View style={{ width: 24, height: 24 }}>
                    <Icon name={ICON_NAMES.Delete} color={alert.alertRed} />
                  </View>
                  <TextLato style={[styles_lookup.choiceText, { color: alert.alertRed }]}>
                    {i18n.t('customize:delete-frame')}
                  </TextLato>
                </View>
              </View>
            </TouchableOpacity>
          )}
          <View style={{ paddingTop: 20 }}>
            <TextLato style={styles_lookup.text_version}>PhotoSì Editor v{APP_VERSION}</TextLato>
          </View>
        </View>
      </View>
    </BottomSheetScrollView>
  );
};

const styles_lookup = StyleSheet.create({
  container: {
    flex: 1,
    gap: 16,
    borderLeftColor: neutrals.coldGrays.ice,
    borderLeftWidth: 2,
  },
  control: {
    width: '100%',
    height: 'auto',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 4,
    gap: 5,
    borderRadius: 8,
  },
  header: {
    paddingTop: 24,
    paddingHorizontal: 24,
    paddingBottom: 16,
    flexDirection: 'row',
    gap: 15,
    alignItems: 'center',
  },
  scrollContainer: {
    paddingHorizontal: 16,
  },
  containerChoices: {
    width: '100%',
    flexDirection: 'row',
    flexWrap: 'wrap',
    rowGap: 24,
    flex: 1,
    columnGap: 10,
  },
  containerChoice: {
    width: '100%',
    height: 50,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  choiceDetails: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 16,
  },
  choiceText: {
    textAlign: 'center',
    fontSize: 16,
    lineHeight: 20,
    color: neutrals.neutralGrays.tarmac,
    fontWeight: 700,
  },
  pill: {
    justifyContent: 'center',
    backgroundColor: neutrals.coldGrays.ice,
    width: 52,
    height: 20,
    borderRadius: 100,
    paddingVertical: 1,
    paddingHorizontal: 2,
  },
  pillTextxt: {
    textAlign: 'center',
    color: brand.main.water,
    fontSize: 10,
    fontWeight: 700,
    lineHeight: 14,
  },
  separator: {
    marginTop: 16,
    borderBottomWidth: 1,
    borderColor: neutrals.neutralGrays.dust,
  },
  titleContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: 5,
    paddingTop: 10,
  },
  title: {
    fontSize: 20,
    fontWeight: '700',
    color: neutrals.black,
  },
  text_version: {
    fontWeight: 400,
    fontSize: 12,
    color: neutrals.neutralGrays.grey,
  },
  icon: {
    width: 24,
    height: 24,
    padding: 2,
  },
  closeIcon: { padding: 5, color: neutrals.neutralGrays.ash },
  arrowIcon: {
    width: 14,
    height: 14,
  },
});
